import ValidateModule from "./ValidateModule";

import type { IEventChannel, ApiOpts } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

const es = global.es;

/**
 * @class PredefinedRangesModule
 */
export default class PredefinedRangesModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;
    
    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        this.get = this.get.bind(this);
        this.list = this.list.bind(this);
        this.count = this.count.bind(this);
        this.create = this.create.bind(this);
        this.update = this.update.bind(this);
        this.remove = this.remove.bind(this);
        this.getDefaultId = this.getDefaultId.bind(this);
        this.setDefaultId = this.setDefaultId.bind(this);
        this.removeDefaultId = this.removeDefaultId.bind(this);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param id
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the projections are loaded
     */
    get(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + id;

        return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    list(criteria, sortOrder, offset, length, opts?: ApiOpts) {
        //return rqBroker.doGet({url: es.api.getListQueryURL(resourceName, criteria, sortOrder, offset, length)});
        return this.cache.doGetList(this.rqBroker, this.resourceName, criteria, sortOrder, offset, length, opts);
    }
    
    /**
     * Get a count of resources matching the given Criteria
     * 
     * @param criteria
     * @param opts
     * 
     * @returns long
     */
    count(criteria, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/count" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");

        return this.rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * POST a new resource to the server (note: id attrib must be -1)
     * 
     * @param resourceObj e.g. 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    create(resourceObj, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }
        
        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName),
            opts: opts,
            data: resourceObj
        }).then((predefinedRanges) => {
            this.cache.clear(this.resourceName, 'list');
            this.cache.setEntity(this.resourceName, predefinedRanges);
            
            return predefinedRanges;
        });
    }
    
    /**
     * PUT update an existing resource on the server
     * 
     * @param resourceObj e.g. 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    update(resourceObj, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + resourceObj.id;

        return this.rqBroker.doPut({
            url: url,
            opts: opts,
            data: resourceObj
        }).then((rs) => {
            this.cache.clear(this.resourceName, 'list');
            this.cache.setEntity(this.resourceName, rs);
            
            return rs;
        });
    }
    
    /**
     * Removes the resource from the server
     * 
     * @param id
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    remove(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + id;

        return this.rqBroker.doDelete({
            url: url,
            opts: opts
        }).then((rs, jqXHR) => {
            this.cache.clear(this.resourceName, 'list');
            this.cache.clear(this.resourceName, id);
            this.cache.clear(this.resourceName + '_default');
            
            return es.util.deferred.resolved();
        });
    }
    
    /**
     * 
     * @param opts 
     * 
     * @returns 
     */
    getDefaultId(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + '/default';

        return this.cache.doGet(this.rqBroker, this.resourceName + '_default', url, -1, opts);
    }
    
    /**
     * 
     * @param defaultId 
     * @param opts 
     * 
     * @returns 
     */
    setDefaultId(defaultId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(defaultId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + '/default';
        
        return this.rqBroker.doPut({
            url: url,
            opts: opts,
            data: defaultId
        }).then((rs) => {
            this.cache.set(this.resourceName + '_default', -1, rs, opts);
            
            return rs;
        });
    }
    
    /**
     * 
     * @param opts 
     * 
     * @returns 
     */
    removeDefaultId(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + '/default';

        return this.rqBroker.doDelete({
            url: url,
            opts: opts
        }).then((rs) => {
            this.cache.clear(this.resourceName + '_default');
            
            return rs;
        });
    }
}
